import { apiClient, tokenStorage } from './apiClient';
import {
  ToDoItem,
  CreateToDoItem,
  Category,
  CreateCategory,
  TimeLogEntry,
  CreateTimeLogEntry,
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  AuthUser,
  SocialProvider,
  SocialLoginPayload,
  ChatThread,
  SendChatMessagePayload,
  SendChatMessageResponse,
  SendChatMessageResult,
  ChatUsage,
} from '../types/api';

// Authentication
export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const { data } = await apiClient.post<AuthResponse>('/auth/login/', credentials);
    tokenStorage.set(data.access, data.refresh);
    return data;
  },

  register: async (credentials: RegisterCredentials): Promise<AuthResponse> => {
    const { data } = await apiClient.post<AuthResponse>('/auth/registration/', credentials);
    tokenStorage.set(data.access, data.refresh);
    return data;
  },

  socialLogin: async (provider: SocialProvider, payload: SocialLoginPayload): Promise<AuthResponse> => {
    const { data } = await apiClient.post<AuthResponse>(`/auth/${provider}/`, payload);
    tokenStorage.set(data.access, data.refresh);
    return data;
  },

  me: async (): Promise<AuthUser> => {
    const { data } = await apiClient.get<AuthUser>('/auth/user/');
    return data;
  },

  updateProfile: async (changes: { full_name: string }): Promise<AuthUser> => {
    const { data } = await apiClient.patch<AuthUser>('/auth/user/', changes);
    return data;
  },

  deleteAccount: async (confirm: string): Promise<void> => {
    await apiClient.post('/auth/account/delete/', { confirm });
    tokenStorage.clear();
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout/', { refresh: tokenStorage.getRefresh() });
    } finally {
      tokenStorage.clear();
    }
  },
};

// To-dos
export const todoApi = {
  getAll: async (): Promise<ToDoItem[]> => {
    const { data } = await apiClient.get<ToDoItem[]>('/todo/todos/');
    return data;
  },

  getById: async (id: number): Promise<ToDoItem> => {
    const { data } = await apiClient.get<ToDoItem>(`/todo/todos/${id}/`);
    return data;
  },

  create: async (todo: CreateToDoItem): Promise<ToDoItem> => {
    const { data } = await apiClient.post<ToDoItem>('/todo/todos/', todo);
    return data;
  },

  update: async (id: number, todo: Partial<CreateToDoItem>): Promise<ToDoItem> => {
    const { data } = await apiClient.patch<ToDoItem>(`/todo/todos/${id}/`, todo);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/todo/todos/${id}/`);
  },
};

// Categories
export const categoryApi = {
  getAll: async (): Promise<Category[]> => {
    const { data } = await apiClient.get<Category[]>('/todo/categories/');
    return data;
  },

  create: async (category: CreateCategory): Promise<Category> => {
    const { data } = await apiClient.post<Category>('/todo/categories/', category);
    return data;
  },

  update: async (id: number, category: Partial<CreateCategory>): Promise<Category> => {
    const { data } = await apiClient.patch<Category>(`/todo/categories/${id}/`, category);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/todo/categories/${id}/`);
  },
};

// Time logs
export const timeLogApi = {
  getAll: async (): Promise<TimeLogEntry[]> => {
    const { data } = await apiClient.get<TimeLogEntry[]>('/time-logger/entries/');
    return data;
  },

  getById: async (id: number): Promise<TimeLogEntry> => {
    const { data } = await apiClient.get<TimeLogEntry>(`/time-logger/entries/${id}/`);
    return data;
  },

  create: async (entry: CreateTimeLogEntry): Promise<TimeLogEntry> => {
    const { data } = await apiClient.post<TimeLogEntry>('/time-logger/entries/', entry);
    return data;
  },

  update: async (id: number, entry: Partial<CreateTimeLogEntry>): Promise<TimeLogEntry> => {
    const { data } = await apiClient.patch<TimeLogEntry>(`/time-logger/entries/${id}/`, entry);
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/time-logger/entries/${id}/`);
  },
};

// AI chat
export const chatApi = {
  listThreads: async (): Promise<ChatThread[]> => {
    const { data } = await apiClient.get<ChatThread[]>('/ai/chat/');
    return data;
  },

  getThread: async (threadId: number): Promise<ChatThread> => {
    const { data } = await apiClient.get<ChatThread>(`/ai/chat/${threadId}/`);
    return data;
  },

  sendMessage: async (payload: SendChatMessagePayload): Promise<SendChatMessageResult> => {
    const { data } = await apiClient.post<SendChatMessageResponse>('/ai/chat/', payload);
    return data.response;
  },

  deleteThread: async (threadId: number): Promise<void> => {
    await apiClient.delete(`/ai/chat/${threadId}/`);
  },

  getUsage: async (): Promise<ChatUsage> => {
    const { data } = await apiClient.get<ChatUsage>('/ai/usage/');
    return data;
  },
};
